import React from 'react'
// libs
import { Field } from 'redux-form'
// components
import { Select } from 'components/FormElements'
// types
import { Validators } from 'utils/validators'
import { CustomSelectProps, IOption } from 'components/FormElements/Select'
import { WrappedFieldInputProps, WrappedFieldProps } from 'redux-form/lib/Field'
// utils
import debounce from 'lodash/debounce'
import isFunction from 'lodash/isFunction'

export interface ReduxSelectProps extends Omit<CustomSelectProps, 'value' | 'onChange' | 'defaultValue'> {
  validate?: Validators
  errorText?: string
  handleSearch?: (value?: string) => void
  onSelectChange?: (value: IOption, input: WrappedFieldInputProps) => void
  blurAfterChange?: () => void
  handleAfterBlur?: () => void
}

const Component: React.FC<ReduxSelectProps & WrappedFieldProps> = ({
  errorText,
  handleSearch = () => {},
  onSelectChange,
  blurAfterChange,
  handleAfterBlur,
  ...rest
}) => {
  const {
    input,
    meta: { touched, error }
  } = rest

  const inputValueRef = React.useRef(null)

  const search = debounce((value?: string) => {
    if (isFunction(handleSearch)) {
      if (value) {
        handleSearch(value)
      } else {
        handleSearch()
      }
    }
  }, 500)

  const onBlur = (value: IOption) => {
    input.onBlur(input.value || value)

    if (inputValueRef.current) {
      search()
    }

    inputValueRef.current = null
  }

  const onInputChange = (value: string) => {
    search(value)
    handleAfterBlur?.()
    inputValueRef.current = value
  }

  const onChange = (value: IOption) => {
    input.onChange(value)

    if (blurAfterChange) {
      onBlur(value)
    }

    onSelectChange?.(value, input)
  }

  const isError = touched && error

  return (
    <Select
      {...rest}
      error={error || errorText}
      isError={isError || errorText}
      name={input.name}
      value={input.value}
      handleBlur={onBlur}
      handleChange={onChange}
      handleFocus={input.onFocus}
      classNamePrefix={'react-select'}
      handleInputChange={onInputChange}
    />
  )
}

const ReduxSelect: React.FC<ReduxSelectProps> = (props) => <Field {...props} component={Component} />

export default ReduxSelect
